import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Select, Button, Pagination, Modal } from "antd";
import { SearchOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import FavoriteButton from "../../components/FavoriteButton";
import ListingThumbnail from "../../components/listings/ListingThumbnail";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorState,
  ListSkeleton,
} from "../../components/states";
import WhiteboardFilter from "../../components/agent/WhiteboardFilter";
import WhiteboardMap from "../../components/agent/WhiteboardMap";
import WantsNeedsCard from "../../components/agent/WantsNeedsCard";
import {
  GET_WHITEBOARD_LISTINGS,
  GET_WHITEBOARD_OPEN_HOUSES,
  GET_WHITEBOARD_WANTS_NEEDS,
} from "../../graphql/agentQueries";
import {
  LISTING_SORT_OPTIONS,
  OPEN_HOUSE_SORT_OPTIONS,
  DEFAULT_FILTERS,
} from "../../constants/whiteboardConstants";
import priceFormat from "../../utils/priceFormat";
import useDebounce from "../../hooks/useDebounce";
import { useSidebar } from "../../context/SidebarContext";

// ==================== LISTING CARD COMPONENT ====================
const ListingCard = ({ listing, onCardClick, companyId, currentUserId }) => {
  // Calculate listing status based on expiration (go-live date)
  const getListingStatus = () => {
    if (!listing.expiration) return { isLive: false, days: 0, isToday: false };

    const goLiveDate = new Date(parseInt(listing.expiration, 10));
    const today = new Date();

    // Reset time for accurate day comparison
    today.setHours(0, 0, 0, 0);
    goLiveDate.setHours(0, 0, 0, 0);

    const diffTime = goLiveDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isLive: diffDays <= 0,
      days: Math.abs(diffDays),
      isToday: diffDays === 0,
    };
  };

  const { isLive, days, isToday } = getListingStatus();

  // Format status text like mobile app
  const renderStatus = () => {
    if (isLive) {
      return (
        <div className="flex flex-col items-center text-center">
          <svg
            className="w-6 h-6 text-[#b4c540] mb-1"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          <span className="text-[#b4c540] font-semibold text-xs">LIVE on MLS</span>
          <span className="text-gray-600 text-xs">
            {isToday ? "TODAY" : `${days} ${days === 1 ? "DAY" : "DAYS"} AGO`}
          </span>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center text-center">
          <svg
            className="w-6 h-6 text-[#3687c9] mb-1"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          <span className="text-[#3687c9] font-semibold text-xs">Going Live In</span>
          <span className="text-gray-600 text-xs">
            {isToday ? "TODAY" : `${days} ${days === 1 ? "DAY" : "DAYS"}`}
          </span>
        </div>
      );
    }
  };

  return (
    <div
      className="relative border-b border-gray-200 py-4 px-4 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onCardClick(listing)}
    >
      {/* Pinned to the row's top-right corner, inside the row padding. */}
      <div
        className="absolute top-2 right-4"
        onClick={(e) => e.stopPropagation()}
      >
        <FavoriteButton
          listingId={listing._id}
          agentId={currentUserId}
          companyId={companyId}
        />
      </div>

      <div className="flex justify-between items-start">
        {/* Left: Photo + Listing Info */}
        <ListingThumbnail photos={listing.imageURLs ?? []} />

        <div className="flex-1 px-4">
          {/* Price */}
          <div className="text-lg font-bold text-black mb-1">
            ${listing.price ? priceFormat(listing.price) : ""}
          </div>

          {/* Address */}
          <div className="text-gray-700 text-sm mb-2">
            {listing.streetAddr} {listing.city} {listing.zip}
          </div>

          {/* Specs: beds, baths, sqft */}
          <div className="flex items-center text-gray-600 text-sm gap-4">
            <span>{listing.beds || 0} bd</span>
            <span>{listing.baths || 0} ba</span>
            <span>{listing.sqFt ? `${listing.sqFt.toLocaleString()} sqft` : "0 sqft"}</span>
          </div>
        </div>

        {/* Right: Status — sits under the pinned heart */}
        <div className="flex flex-col items-end justify-center pt-6">
          {renderStatus()}
        </div>
      </div>
    </div>
  );
};

// ==================== WANTS & NEEDS CARD COMPONENT ====================
// Layout matches mobile app screenshot:
// Row 1: Buyer ID [blue] | Agent: [agent name in blue]
// Row 2: City: [city]    | Price: $min - $max
// Row 3: BDR: X+   BTH: X+   SQFT: min - max
const WantsNeedsListCard = ({ criteria, onCardClick }) => {
  // Format price range
  const formatPrice = () => {
    if (criteria.minPrice && criteria.maxPrice) {
      return `$${priceFormat(criteria.minPrice)} - $${priceFormat(criteria.maxPrice)}`;
    } else if (criteria.minPrice) {
      return `$${priceFormat(criteria.minPrice)}+`;
    } else if (criteria.maxPrice) {
      return `Up to $${priceFormat(criteria.maxPrice)}`;
    }
    return "Any";
  };

  // Format sqft range
  const formatSqft = () => {
    if (criteria.minSqFt && criteria.maxSqFt) {
      return `${criteria.minSqFt} - ${criteria.maxSqFt}`;
    } else if (criteria.minSqFt) {
      return `${criteria.minSqFt}+`;
    } else if (criteria.maxSqFt) {
      return `Up to ${criteria.maxSqFt}`;
    }
    return "Any";
  };

  return (
    <div
      className="border-b border-gray-200 py-4 px-4 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onCardClick(criteria)}
    >
      {/* Row 1: Buyer ID and Agent */}
      <div className="flex justify-between items-center mb-2">
        <div>
          <span className="font-bold text-black">Buyer ID </span>
          <span className="text-[#3687c9] font-medium">
            {(criteria.buyerId || criteria._id)?.slice(-4).toUpperCase()}
          </span>
        </div>
        <div>
          <span className="font-bold text-black">Agent: </span>
          <span className="text-[#3687c9]">
            {criteria.agentName || ""}
          </span>
        </div>
      </div>

      {/* Row 2: City and Price */}
      <div className="flex justify-between items-center mb-2 text-sm">
        <div className="text-gray-700">
          <span>City: </span>
          <span>{criteria.cities?.join(", ") || "Any"}</span>
        </div>
        <div className="text-gray-700">
          <span>Price: </span>
          <span>{formatPrice()}</span>
        </div>
      </div>

      {/* Row 3: BDR, BTH, SQFT */}
      <div className="flex items-center text-gray-600 text-sm gap-6">
        <span>BDR: {criteria.minBeds || 0}+</span>
        <span>BTH: {criteria.minBath || 0}+</span>
        <span>SQFT: {formatSqft()}</span>
      </div>
    </div>
  );
};

// ==================== OPEN HOUSE CARD COMPONENT ====================
// Layout matches mobile app screenshot:
// Row 1: Open: [date] - [time range] | [Request to Host button if needHost]
// Row 2: [address]
// Row 3: [$price]
const OpenHouseListCard = ({ listing, onCardClick, onRequestToHost }) => {
  // Format date like mobile app (e.g., "Sat. 2/15/26")
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    // Parse the timestamp - handle ISO strings, numeric strings, or numbers
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "";

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = days[date.getDay()];
    const month = date.getMonth() + 1;
    const dayOfMonth = date.getDate();
    const year = date.getFullYear().toString().substr(-2);

    return `${dayOfWeek}. ${month}/${dayOfMonth}/${year}`;
  };

  // Format time range like mobile app (e.g., "10:00 AM - 2:00 PM")
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return "";
    // Parse timestamps - handle ISO strings, numeric strings, or numbers
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";

    const options = { hour: "numeric", minute: "2-digit", hour12: true };
    return `${start.toLocaleTimeString("en-US", options)} - ${end.toLocaleTimeString("en-US", options)}`;
  };

  // Calculate listing status based on expiration (go-live date)
  const isLiveOnMLS = () => {
    if (!listing.expiration) return false;
    const goLiveDate = new Date(parseInt(listing.expiration, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    goLiveDate.setHours(0, 0, 0, 0);
    return goLiveDate <= today;
  };

  const isLive = isLiveOnMLS();
  const colors = isLive ? "#b4c540" : "#4E75E1";

  // Check if any open house needs a host
  const needsHost = listing.openHouses?.some((oh) => oh.needHost);

  // Format all open house dates
  const renderOpenHouseTimes = () => {
    if (!listing.openHouses || listing.openHouses.length === 0) return null;

    return listing.openHouses.map((house, idx) => (
      <span key={idx}>
        {idx > 0 && <br />}
        {formatDate(house.startTime)} - {formatTimeRange(house.startTime, house.endTime)}
      </span>
    ));
  };

  return (
    <div
      className="border-b border-gray-200 py-4 px-4 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onCardClick(listing)}
    >
      <div className="flex items-start">
        {/* Left: Photo — mirrors the Whiteboard listing row */}
        <ListingThumbnail photos={listing.imageURLs ?? []} />

        <div className="flex-1 pl-4">
          {/* Open House Times — spans the full remaining width */}
          <div className="text-sm font-semibold mb-1" style={{ color: colors }}>
            <span>Open: </span>
            {renderOpenHouseTimes()}
          </div>

          {/* Details on the left, Request-to-Host on the right */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              {/* Address */}
              <div className="text-gray-700 text-sm mb-1">
                {listing.streetAddr} {listing.city} {listing.state} {listing.zip}
              </div>

              {/* Price */}
              <div className="text-gray-700 text-sm">
                ${listing.price ? priceFormat(listing.price) : ""}
              </div>
            </div>

            {needsHost && (
              <button
                className="px-4 py-2 text-white text-xs font-semibold rounded-full whitespace-nowrap"
                style={{ backgroundColor: colors }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestToHost(listing);
                }}
              >
                Request to Host
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== EMPTY STATE ====================
// All three tabs share one empty shape; only the copy differs. A search-driven
// empty offers a way out instead of the generic "nothing here yet" hint.
const WhiteboardEmpty = ({ search, onClearSearch, title, description }) =>
  search ? (
    <EmptyState
      title="No results found"
      description={`Nothing matched "${search}".`}
      actionLabel="Clear search"
      onAction={onClearSearch}
    />
  ) : (
    <EmptyState title={title} description={description} />
  );

// ==================== AGENT CONTACT MODAL ====================
const AgentContactModal = ({ open, onClose, agent }) => {
  if (!agent) return null;

  return (
    <Modal
      title="Agent Contact Information"
      open={open}
      onCancel={onClose}
      width={400}
      centered
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{ backgroundColor: "#b4c540", borderColor: "#b4c540", width: "100%" }}
        >
          Close
        </Button>,
      ]}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        {agent.phone && (
          <div className="flex items-center gap-2 text-[#3687c9] text-lg">
            <PhoneOutlined />
            <span>{agent.phone}</span>
          </div>
        )}
        {agent.email && (
          <a
            href={`mailto:${agent.email}`}
            className="flex items-center gap-2 text-[#3687c9] text-lg hover:underline"
          >
            <MailOutlined />
            {agent.email}
          </a>
        )}
        {agent.firstName && (
          <div className="text-gray-600 text-sm">
            {agent.firstName} {agent.lastName}
          </div>
        )}
      </div>
    </Modal>
  );
};

// ==================== MAIN COMPONENT ====================
const AgentWhiteboard = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);
  const { collapsed } = useSidebar();

  // Safe parse of user info
  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem("userInfo"));
  } catch {
    currentUser = null;
  }

  // Tab state - "listings", "wantsNeeds", or "openHouses"
  const [activeTab, setActiveTab] = useState("listings");

  // View state (list or map)
  const [viewMode, setViewMode] = useState("list");

  // Filter, sort, search state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState("new-old");
  const [wantsNeedsSortBy, setWantsNeedsSortBy] = useState("new-old");
  const [openHouseSortBy, setOpenHouseSortBy] = useState("upcoming");
  const [searchText, setSearchText] = useState("");
  const [wantsNeedsSearch, setWantsNeedsSearch] = useState("");
  const [openHouseSearch, setOpenHouseSearch] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);
  const debouncedWantsSearch = useDebounce(wantsNeedsSearch, 500);
  const debouncedOpenHouseSearch = useDebounce(openHouseSearch, 500);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
  });

  // Wants & Needs detail modal
  const [selectedCriteria, setSelectedCriteria] = useState(null);

  // Open House - Agent Contact modal
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Build filter input for GraphQL
  const buildFilterInput = () => {
    const input = {};
    if (filters.minPrice) input.minPrice = filters.minPrice;
    if (filters.maxPrice) input.maxPrice = filters.maxPrice;
    if (filters.beds > 0) input.beds = filters.beds;
    if (filters.baths > 0) input.baths = filters.baths;
    if (filters.minsqFt) input.minsqFt = filters.minsqFt;
    if (filters.maxsqFt) input.maxsqFt = filters.maxsqFt;
    if (filters.minLotSize) input.minLotSize = filters.minLotSize;
    if (filters.maxLotSize) input.maxLotSize = filters.maxLotSize;
    if (filters.minYearBuilt) input.minYearBuilt = filters.minYearBuilt;
    if (filters.maxYearBuilt) input.maxYearBuilt = filters.maxYearBuilt;
    return Object.keys(input).length > 0 ? input : undefined;
  };

  // Reset pagination when filters/sort/search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters, sortBy, debouncedSearch]);

  // ========== QUERIES ==========

  // Listings query (used for both Listings tab)
  const {
    loading: listingsLoading,
    error: listingsError,
    data: listingsData,
    refetch: refetchListings,
  } = useQuery(GET_WHITEBOARD_LISTINGS, {
    variables: {
      companyId,
      listingInput: buildFilterInput(),
      pagination: { page: pagination.page, limit: pagination.limit },
      searchText: debouncedSearch || undefined,
      sortBy: sortBy,
    },
    fetchPolicy: "network-only",
    skip: activeTab !== "listings",
  });

  // Wants & Needs query
  const {
    loading: wantsNeedsLoading,
    error: wantsNeedsError,
    data: wantsNeedsData,
    refetch: refetchWantsNeeds,
  } = useQuery(GET_WHITEBOARD_WANTS_NEEDS, {
    variables: {
      companyId,
      search: debouncedWantsSearch || undefined,
      sortBy: wantsNeedsSortBy,
    },
    fetchPolicy: "network-only",
    skip: activeTab !== "wantsNeeds",
  });

  // Open Houses query
  const {
    loading: openHousesLoading,
    error: openHousesError,
    data: openHousesData,
    refetch: refetchOpenHouses,
  } = useQuery(GET_WHITEBOARD_OPEN_HOUSES, {
    variables: {
      companyId,
      sortBy: openHouseSortBy,
    },
    fetchPolicy: "network-only",
    skip: activeTab !== "openHouses",
  });

  // ========== DATA PROCESSING ==========

  const listings = listingsData?.getCompanyListings?.listing || [];
  const totalCount = listingsData?.getCompanyListings?.totalCount || 0;
  const wantsNeeds = wantsNeedsData?.getCriteriaListingByCompany || [];
  const openHousesRaw = openHousesData?.getCompanyOpenHouseListings || [];

  // Filter open houses by search (client-side filtering like mobile app)
  const openHouses = debouncedOpenHouseSearch
    ? openHousesRaw.filter((listing) => {
        const search = debouncedOpenHouseSearch.toLowerCase();
        return (
          listing.streetAddr?.toLowerCase().includes(search) ||
          listing.city?.toLowerCase().includes(search) ||
          listing.zip?.toLowerCase().includes(search)
        );
      })
    : openHousesRaw;

  // Update pagination total when data changes
  useEffect(() => {
    if (listingsData?.getCompanyListings) {
      setPagination((prev) => ({ ...prev, totalCount }));
    }
  }, [listingsData, totalCount]);

  // Handle errors
  useEffect(() => {
    if (listingsError) toast("error", listingsError.message);
    if (wantsNeedsError) toast("error", wantsNeedsError.message);
    if (openHousesError) toast("error", openHousesError.message);
  }, [listingsError, wantsNeedsError, openHousesError, toast]);

  // ========== HANDLERS ==========

  const handleViewListing = (listing) => {
    navigate(`/company/${companyId}/agent/listings/${listing._id}/view`);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setViewMode("list");
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  // Handle "Request to Host" button click - opens agent contact modal
  const handleRequestToHost = (listing) => {
    setSelectedAgent(listing.user);
  };

  // ========== RENDER ==========

  const isListingsTab = activeTab === "listings";
  const isOpenHousesTab = activeTab === "openHouses";
  const isWantsNeedsTab = activeTab === "wantsNeeds";

  // Each tab owns its own query, so the state trio is resolved per active tab.
  const currentLoading = isListingsTab
    ? listingsLoading
    : isOpenHousesTab
    ? openHousesLoading
    : wantsNeedsLoading;

  const currentRows = isListingsTab
    ? listings
    : isOpenHousesTab
    ? openHouses
    : wantsNeeds;

  const currentError = isListingsTab
    ? listingsError
    : isOpenHousesTab
    ? openHousesError
    : wantsNeedsError;

  const currentRefetch = isListingsTab
    ? refetchListings
    : isOpenHousesTab
    ? refetchOpenHouses
    : refetchWantsNeeds;

  const currentSearch = isListingsTab
    ? searchText
    : isOpenHousesTab
    ? openHouseSearch
    : wantsNeedsSearch;

  const clearCurrentSearch = () => {
    if (isListingsTab) setSearchText("");
    else if (isOpenHousesTab) setOpenHouseSearch("");
    else setWantsNeedsSearch("");
  };

  const firstLoad = currentLoading && !currentRows.length;

  return (
    <div id="agent-whiteboard" className="pb-20">
      <Breadcrumb pageName="Whiteboard" />

      <div className="pb-6">
        <Card>
          {/* Header Controls - Mobile App Style */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {/* Filter Button */}
              {isListingsTab && (
                <WhiteboardFilter
                  filters={filters}
                  onFilterChange={setFilters}
                  onClearFilters={handleClearFilters}
                />
              )}

              {/* Search Input */}
              <div className="flex-1">
                <Input
                  placeholder={
                    isListingsTab
                      ? "City, zip code or address"
                      : isOpenHousesTab
                      ? "City, zip code or address"
                      : "Search by city..."
                  }
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={
                    isListingsTab
                      ? searchText
                      : isOpenHousesTab
                      ? openHouseSearch
                      : wantsNeedsSearch
                  }
                  onChange={(e) =>
                    isListingsTab
                      ? setSearchText(e.target.value)
                      : isOpenHousesTab
                      ? setOpenHouseSearch(e.target.value)
                      : setWantsNeedsSearch(e.target.value)
                  }
                  allowClear
                  className="border-0 border-b-2 border-[#b4c540] rounded-none focus:shadow-none"
                />
              </div>

              {/* Map Toggle - only for listings */}
              {isListingsTab && (
                <Button
                  type="text"
                  className="text-[#3687c9] font-medium"
                  onClick={() => setViewMode(viewMode === "list" ? "map" : "list")}
                >
                  {viewMode === "list" ? "Map" : "List"}
                </Button>
              )}
            </div>

            {/* Sort Indicator */}
            <div className="mt-3">
              <Select
                value={
                  isListingsTab
                    ? sortBy
                    : isOpenHousesTab
                    ? openHouseSortBy
                    : wantsNeedsSortBy
                }
                onChange={
                  isListingsTab
                    ? setSortBy
                    : isOpenHousesTab
                    ? setOpenHouseSortBy
                    : setWantsNeedsSortBy
                }
                bordered={false}
                className="text-[#b4c540] font-medium"
                popupMatchSelectWidth={false}
                options={
                  isListingsTab
                    ? LISTING_SORT_OPTIONS.map((opt) => ({
                        ...opt,
                        label: `Sort: ${opt.label}`,
                      }))
                    : isOpenHousesTab
                    ? OPEN_HOUSE_SORT_OPTIONS.map((opt) => ({
                        ...opt,
                        label: `Sort: ${opt.label}`,
                      }))
                    : [
                        { value: "new-old", label: "Sort: Newest" },
                        { value: "old-new", label: "Sort: Oldest" },
                      ]
                }
              />
            </div>
          </div>

          {/* Content — keyed so the skeleton -> data swap remounts and eases in */}
          <div
            key={firstLoad ? "skeleton" : "data"}
            className="animate-riseIn"
          >
            {firstLoad ? (
              <ListSkeleton rows={pagination.limit} />
            ) : currentError && !currentRows.length ? (
              <ErrorState onRetry={() => currentRefetch()} />
            ) : isListingsTab ? (
              // Listings View
              viewMode === "map" ? (
                <WhiteboardMap
                  listings={listings}
                  onListingClick={handleViewListing}
                  loading={listingsLoading}
                />
              ) : listings.length === 0 ? (
                <WhiteboardEmpty
                  search={currentSearch}
                  onClearSearch={clearCurrentSearch}
                  title="No listings found"
                  description="Listings added by your company will appear here."
                />
              ) : (
                <>
                  {/* Listing Cards */}
                  <div className="divide-y divide-gray-200">
                    {listings.map((listing) => (
                      <ListingCard
                        key={listing._id}
                        listing={listing}
                        onCardClick={handleViewListing}
                        companyId={companyId}
                        currentUserId={currentUser?.id}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalCount > pagination.limit && (
                    <div className="flex justify-center py-4 border-t">
                      <Pagination
                        current={pagination.page}
                        pageSize={pagination.limit}
                        total={totalCount}
                        onChange={handlePageChange}
                        showSizeChanger
                        showTotal={(total) => `Total ${total} listings`}
                      />
                    </div>
                  )}
                </>
              )
            ) : isOpenHousesTab ? (
              // Open Houses View
              openHouses.length === 0 ? (
                <WhiteboardEmpty
                  search={currentSearch}
                  onClearSearch={clearCurrentSearch}
                  title="No open houses found"
                  description="Scheduled open houses will appear here."
                />
              ) : (
                <div className="divide-y divide-gray-200">
                  {openHouses.map((listing) => (
                    <OpenHouseListCard
                      key={listing._id}
                      listing={listing}
                      onCardClick={handleViewListing}
                      onRequestToHost={handleRequestToHost}
                    />
                  ))}
                </div>
              )
            ) : (
              // Wants & Needs View
              wantsNeeds.length === 0 ? (
                <WhiteboardEmpty
                  search={currentSearch}
                  onClearSearch={clearCurrentSearch}
                  title="No wants & needs found"
                  description="Buyer criteria added by your company will appear here."
                />
              ) : (
                <div className="divide-y divide-gray-200">
                  {wantsNeeds.map((criteria) => (
                    <WantsNeedsListCard
                      key={criteria._id}
                      criteria={criteria}
                      onCardClick={setSelectedCriteria}
                    />
                  ))}
                </div>
              )
            )}
          </div>

        </Card>
      </div>

      {/* Fixed Bottom Tab Toggle — starts where the sidebar ends: nothing below
          lg (drawer), then the rail's or the full sidebar's width. */}
      <div
        className={`fixed bottom-0 left-0 right-0 flex justify-center py-4 bg-white border-t shadow-lg z-10 ${
          collapsed ? "lg:left-20" : "lg:left-64"
        }`}
      >
        <div className="inline-flex bg-gray-200 rounded-full p-1">
          <button
            onClick={() => handleTabChange("listings")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "listings"
                ? "bg-[#b4c540] text-white"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Listings
          </button>
          <button
            onClick={() => handleTabChange("wantsNeeds")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "wantsNeeds"
                ? "bg-[#b4c540] text-white"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Wants & Needs
          </button>
          <button
            onClick={() => handleTabChange("openHouses")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "openHouses"
                ? "bg-[#b4c540] text-white"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Open Houses
          </button>
        </div>
      </div>

      {/* Wants & Needs Detail Modal */}
      <WantsNeedsCard
        criteria={selectedCriteria}
        open={!!selectedCriteria}
        onClose={() => setSelectedCriteria(null)}
      />

      {/* Agent Contact Modal (for Open House "Request to Host") */}
      <AgentContactModal
        open={!!selectedAgent}
        onClose={() => setSelectedAgent(null)}
        agent={selectedAgent}
      />
    </div>
  );
};

export default AgentWhiteboard;
